import { useEffect, useState } from "react";
import { Permission, Person, CreatePersonData } from "@forgepoint/shared";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CompanySelector } from "@/components/CompanySelector";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useHasPermission } from "@/store/authStore";
import { usePeopleStore } from "./peopleStore";
import { toast } from "sonner";

/** Form state for creating a person. */
type PersonFormState = {
  firstName: string;
  lastName: string;
  email: string;
  companyId: string | null;
  isPrimaryCompanyContact: boolean;
};

/**
 * Props for PersonCreateSheet.
 */
export interface PersonCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (person: Person) => void;
}

/**
 * Returns the default empty form state for person creation.
 * @returns A PersonFormState with all fields set to their defaults
 */
const getInitialFormState = (): PersonFormState => ({
  firstName: "",
  lastName: "",
  email: "",
  companyId: null,
  isPrimaryCompanyContact: false,
});

/**
 * A sheet component for creating a person. Renders an empty form and creates
 * the person via the store.
 * @param props - Open state, change handler, and saved callback
 * @returns The PersonCreateSheet component
 */
export default function PersonCreateSheet({
  open,
  onOpenChange,
  onSaved,
}: PersonCreateSheetProps) {
  const { createPerson } = usePeopleStore();
  const { companies, fetchCompanies: fetchCompaniesList } = useCompaniesStore();
  // Without edit.all, a person can only be created inside a company the user
  // owns, so a company is required. The backend enforces the ownership check.
  const requiresCompany = !useHasPermission(Permission.PEOPLE_EDIT_ALL);

  const [formData, setFormData] = useState<PersonFormState>(
    getInitialFormState(),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (companies.size === 0) {
      fetchCompaniesList();
    }
  }, [companies.size, fetchCompaniesList]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormData(getInitialFormState());
  }, [open]);

  /**
   * Validates the form and creates a new person via the store.
   */
  const handleSave = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error("First name and last name are required");
      return;
    }
    if (formData.isPrimaryCompanyContact && !formData.companyId) {
      toast.error("Select a company before marking as primary contact");
      return;
    }
    if (requiresCompany && !formData.companyId) {
      toast.error("Select a company you own to create this person");
      return;
    }

    setIsSaving(true);
    try {
      const payload: CreatePersonData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
      };
      if (formData.email.trim()) {
        payload.email = formData.email.trim();
      }
      if (formData.companyId) {
        payload.companyId = formData.companyId;
      }
      if (formData.isPrimaryCompanyContact) {
        payload.isPrimaryCompanyContact = true;
      }

      const result = await createPerson(payload);
      if (result.person) {
        toast.success("Person created successfully");
        onSaved(result.person);
      } else {
        toast.error(result.error ?? "Failed to create person");
      }
    } catch (_error) {
      toast.error("Failed to create person");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Create New Person</SheetTitle>
          <SheetDescription>Add a new person to your contacts</SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="create-firstName">First Name *</Label>
            <Input
              id="create-firstName"
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              placeholder="First name"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-lastName">Last Name *</Label>
            <Input
              id="create-lastName"
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              placeholder="Last name"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-email">Email</Label>
            <Input
              id="create-email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  email: e.target.value,
                }))
              }
              placeholder="Email address (optional)"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-company">
              Company{requiresCompany ? " *" : ""}
            </Label>
            <CompanySelector
              currentlySelectedId={formData.companyId}
              onSelectId={(companyId) =>
                setFormData((current) => ({
                  ...current,
                  companyId,
                  isPrimaryCompanyContact: companyId
                    ? current.isPrimaryCompanyContact
                    : false,
                }))
              }
              placeholder={
                requiresCompany
                  ? "Select a company you own"
                  : "Select company (optional)"
              }
            />
          </div>
          <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <Label htmlFor="create-primary">Primary Company Contact</Label>
              <p className="text-sm text-muted-foreground">
                Only one person can be primary per company. Setting this will
                clear the primary status of any other person in the same
                company.
              </p>
            </div>
            <Switch
              id="create-primary"
              checked={formData.isPrimaryCompanyContact}
              onCheckedChange={(checked) =>
                setFormData((current) => ({
                  ...current,
                  isPrimaryCompanyContact: checked,
                }))
              }
              disabled={!formData.companyId}
            />
          </div>
        </div>

        <SheetFooter className="flex-col gap-2 sm:flex-row">
          <SheetClose asChild>
            <Button
              variant="outline"
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </SheetClose>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
            Create Person
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
